import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { isDaysInShowRanges } from './parsePriceAlertPayload';

// Hoisted so every consumer passes the same options object identity to the hook.
const PRICE_ALERTS_FLAG_OPTIONS = { consentKey: 'accommodationsPriceAlerts', consentFeatureUsesPayload: true } as const;

/**
 * The single gate for every price-alerts surface (the accommodations banner, the two Guest Guide
 * actions, and the reminder-modal trigger).
 *
 * `accommodationsPriceAlerts` is both an Amplitude experiment and a `UserConsentKey` of the same
 * name, so one `useIsFeatureEnabled` call resolves both: the per-event consent override wins when
 * set (`true` → treatment, `false` → control) and otherwise the Amplitude assignment decides.
 * `consentFeatureUsesPayload` keeps the experiment payload (`showRanges`) flowing on the consent
 * path so the day-range gate below behaves the same either way.
 *
 * Returns `false` while remote flags are still loading, so no surface flashes in before the
 * per-event override is known.
 *
 * Note on the forced-*on* path: consent only forces the variant, not the payload — Amplitude still
 * returns the payload for the arm each guest is bucketed into. Guests bucketed control have no
 * `showRanges`, and `isDaysInShowRanges` treats a missing payload as "show for all days", so on an
 * event forced on, some guests see the surfaces at every horizon rather than only inside the
 * experiment's ranges. Fine for an explicit opt-in; the opt-out path (consent `false`) is exact.
 *
 * @param daysUntilEvent Days until the event, or `undefined` when the event has no finalized date
 *                       (an unknown date is not gated — see `isDaysInShowRanges`).
 */
export const usePriceAlertsActive = (daysUntilEvent: number | undefined): boolean => {
  const [isEnabled, , , payload] = useIsFeatureEnabled('accommodationsPriceAlerts', PRICE_ALERTS_FLAG_OPTIONS);

  return isEnabled && isDaysInShowRanges(daysUntilEvent, payload);
};
